import { CSSProperties, ReactNode } from 'react'
import styled from '@emotion/styled'
import { DESIGN_TOKENS } from 'constants/color.constants'

const TYPOGRAPHY_VARIANTS = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    body1: 'p',
    body2: 'p',
    caption: 'p',
}

const HEADING_VARIANTS = [
    TYPOGRAPHY_VARIANTS.h1,
    TYPOGRAPHY_VARIANTS.h2,
    TYPOGRAPHY_VARIANTS.h3,
    TYPOGRAPHY_VARIANTS.h4,
]

const TYPOGRAPHY_COLORS = {
    accent: DESIGN_TOKENS.text.accentForeground,
    primary: DESIGN_TOKENS.text.foreground,
    secondary: DESIGN_TOKENS.text.mutedForeground,
    success: DESIGN_TOKENS.text.success,
    error: DESIGN_TOKENS.text.error,
} as const

const TYPOGRAPHY_THEME_COLORS = {
    dark: TYPOGRAPHY_COLORS,
    light: {
        ...TYPOGRAPHY_COLORS,
        primary: DESIGN_TOKENS.lightMode.text.foreground,
        secondary: DESIGN_TOKENS.lightMode.text.mutedForeground,
    },
} as const

export type TypographyVariant =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'body1'
    | 'body2'
    | 'caption'

export type TypographyProps = {
    isBold?: boolean
    isItalic?: boolean
    noWrap?: boolean
    shouldTruncate?: boolean
    color: keyof typeof TYPOGRAPHY_COLORS
    variant: TypographyVariant
    className?: string
    textAlign?: CSSProperties['textAlign']
    themeVariant?: 'dark' | 'light'
    children: ReactNode
}

const Typography = ({
    variant,
    // headings default to bold
    isBold = HEADING_VARIANTS.includes(variant),
    isItalic = false,
    noWrap = false,
    shouldTruncate = false,
    color,
    className,
    textAlign,
    themeVariant = 'dark',
    children,
}: TypographyProps) => {
    // @ts-ignore
    const StyledTypography = styled[TYPOGRAPHY_VARIANTS[variant]]<{
        isBold: boolean
        shouldTruncate: boolean
        color: keyof typeof TYPOGRAPHY_COLORS
        variant: 'h1' | 'body2'
    }>`
        margin: 0;
        color: ${TYPOGRAPHY_THEME_COLORS[themeVariant][color]};
        font-style: ${isItalic ? 'italic' : 'normal'};
        font-family: Inter;

        ${isItalic &&
        `
            padding-right: 0.2rem;
        `}

        ${shouldTruncate &&
        `
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;`}

        ${noWrap && `white-space: nowrap;`}

        ${textAlign && `text-align: ${textAlign};`}

        ${() => {
            switch (variant) {
                case 'h1':
                    return `
                    font-size: 3rem;
                    line-height: 3.6rem;
                    letter-spacing: -0.09rem;
                    font-weight: ${isBold ? '700' : '500'};
                `
                case 'h2':
                    return `
                    font-size: 2.6rem;
                    line-height: 3.2rem;
                    letter-spacing: -0.09rem;
                    font-weight: ${isBold ? '700' : '500'};
                `
                case 'h3':
                    return `
                    font-size: 2.2rem;
                    line-height: 2.8rem;
                    letter-spacing: -0.04rem;
                    font-weight: ${isBold ? '700' : '500'};
                `
                case 'h4':
                    return `
                    font-size: 2rem;
                    line-height: 2.6rem;
                    letter-spacing: -0.04rem;
                    font-weight: ${isBold ? '700' : '500'};
                `
                case 'body1':
                    return `
                    font-size: 1.6rem;
                    line-height: 2.4rem;
                    letter-spacing: -0.04rem;
                    font-weight: ${isBold ? '500' : '400'};
                `
                case 'body2':
                    return `
                    font-size: 1.4rem;
                    line-height: 2rem;
                    letter-spacing: -0.04rem;
                    font-weight: ${isBold ? '500' : '400'};
                `
                case 'caption':
                    return `
                    font-size: 1.2rem;
                    line-height: 1.8rem;
                    letter-spacing: -0.02rem;
                    font-weight: ${isBold ? '500' : '400'};
                `
                default:
                    return ''
            }
        }}
    `

    return (
        <StyledTypography
            isBold={isBold}
            shouldTruncate={shouldTruncate}
            color={color}
            variant={variant}
            className={className}
        >
            {children}
        </StyledTypography>
    )
}

export default Typography
